import CustomTooltip from '../../../shared/CustomTooltip';
import { zodResolver } from '@hookform/resolvers/zod';
import { HelpOutline } from '@mui/icons-material';
import {
    Button,
    Chip,
    Grid,
    InputLabel,
    List,
    ListItem,
    Stack,
    Typography,
    useMediaQuery,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { alpha, useTheme } from '@mui/material/styles';
import { format, startOfDay } from 'date-fns';
import React, { useState } from 'react';
import type { DateRange } from 'react-day-picker';
import { DayPicker, type DayButtonProps } from 'react-day-picker';
import 'react-day-picker/style.css';
import { useForm, useFormContext, useWatch } from 'react-hook-form';
import * as z from 'zod';
import { camelCaseToSentenceCase } from '../../../../shared/utils';
import FormAutocomplete from '../../../shared/FormAutocomplete';
import { FormValues } from '../../../types/experience';
import { ModifiedSpace } from './IndividualSpace';

const getCss = (theme: Theme) => `
    .rdp-root {
        font-family: 'Open Sans';
        --rdp-accent-color: ${theme.palette.primary.main};
        --rdp-accent-background-color: ${alpha(
            theme.palette.primary.main,
            0.08,
        )};
        --rdp-day_button-width: 40px;
        --rdp-day_button-height: 40px;
        --rdp-day-width: 40px;
        --rdp-day-height: 40px;
        font-size: 14px;
        color: ${theme.palette.text.primary};
    }

    .rdp-range_middle {
        color: ${theme.palette.primary.main};
    }

    .rdp-selected {
        font-size: 14px;
    }

    .rdp-footer {
        font-size: 14px;
    }

    .rdp-months {
        flex-direction: row;
        gap: 24px;
    }

    @media (max-width: 770px) {
        .rdp-months {
            flex-direction: column;
            gap: 0px;
        }
    }
`;

const SpaceAvailability = ({
    showAvailability,
}: {
    showAvailability: boolean;
}) => {
    const { setValue, control: mainControl } = useFormContext();
    const isMobile = useMediaQuery('(max-width: 770px)');
    const theme = useTheme();

    const schema = z.object({
        from: z.string(),
        to: z.string(),
        reason: z.string().min(1, { message: 'Required' }),
        demo_id: z.number().optional().nullable(),
    });

    type UnavailablePeriod = z.infer<typeof schema>;
    const {
        reset,
        control,
        watch,
        setValue: setValueLocal,
        formState: { isDirty, isLoading },
        handleSubmit,
    } = useForm<UnavailablePeriod>({
        resolver: zodResolver(schema),
        defaultValues: {
            from: '',
            to: '',
            reason: '',
        },
    });

    const unavailablePeriods = useWatch({
        control: mainControl,
        name: 'unavailablePeriods',
    });

    const [draftRange, setDraftRange] = useState<DateRange | undefined>();

    type Period = {
        from: string;
        to: string;
        reason: string;
        demo_id?: number | null;
    };

    const formatChipDate = (value: string) => {
        // Stored as ISO yyyy-MM-dd; display as dd/MM/yyyy
        const parsed = new Date(`${value}T00:00:00`);
        return Number.isNaN(parsed.getTime())
            ? value
            : format(parsed, 'dd/MM/yyyy');
    };

    const onSubmit = (data: UnavailablePeriod) => {
        addPeriodFromRange(draftRange, data.reason);
        reset({ from: '', to: '', reason: '' });
    };

    const onNestedSubmit = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (event && typeof event.stopPropagation === 'function') {
            event.stopPropagation();
        }

        return handleSubmit(onSubmit)(event as any);
    };

    const addPeriodFromRange = (
        range: DateRange | undefined,
        reason: string,
    ) => {
        if (!range?.from) return;

        const newPeriod: Period = {
            from: format(range.from, 'yyyy-MM-dd'),
            to: format(range.to ?? range.from, 'yyyy-MM-dd'),
            reason: reason,
            demo_id: Math.random(),
        };

        const updatedPeriods = [...unavailablePeriods];

        updatedPeriods.push(newPeriod);
        updatedPeriods.sort((a, b) => a.from.localeCompare(b.from));
        setValue('unavailablePeriods', updatedPeriods, { shouldDirty: true });

        setDraftRange(undefined);
    };

    const CustomDayButton = (props: DayButtonProps) => {
        const { day, modifiers, ...buttonProps } = props;
        if (modifiers.disabled) {
            const dateString = format(day.date, 'yyyy-MM-dd');
            const unavailablePeriod = unavailablePeriods?.find(
                (p: Period) => dateString >= p.from && dateString <= p.to,
            );

            const reason = camelCaseToSentenceCase(
                unavailablePeriod?.reason || '',
            );

            return (
                <CustomTooltip title={reason} arrow>
                    <button {...buttonProps} />
                </CustomTooltip>
            );
        }
        return <button {...buttonProps} />;
    };

    return (
        <Stack
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}
            spacing={1}
        >
            {showAvailability && (
                <>
                    <style>{getCss(theme)}</style>
                    <Grid container sx={{ width: '100%', py: 1 }}>
                        <Grid container item justifyContent='center'>
                            <DayPicker
                                mode='range'
                                excludeDisabled
                                numberOfMonths={isMobile ? 1 : 2}
                                selected={draftRange}
                                onSelect={(range) => {
                                    setDraftRange(range);
                                    if (range?.from) {
                                        setValueLocal(
                                            'from',
                                            format(range.from, 'yyyy-MM-dd'),
                                        );
                                        setValueLocal(
                                            'to',
                                            format(
                                                range.to ?? range.from,
                                                'yyyy-MM-dd',
                                            ),
                                        );
                                    } else {
                                        setValueLocal('from', '');
                                        setValueLocal('to', '');
                                    }
                                }}
                                disabled={[
                                    ...unavailablePeriods,
                                    {
                                        before: new Date(
                                            new Date().getTime() +
                                                24 * 60 * 60 * 1000,
                                        ),
                                    },
                                ]}
                                modifiersStyles={{
                                    disabled: {
                                        color: alpha(
                                            theme.palette.text.primary,
                                            0.6,
                                        ),
                                    },
                                }}
                                components={{
                                    DayButton: CustomDayButton,
                                }}
                            />
                        </Grid>
                        <Grid container item>
                            <FormAutocomplete
                                placeholder='Unavailability Reason'
                                name='reason'
                                control={control}
                                fullWidth={true}
                                options={[
                                    {
                                        value: 'maintenance',
                                        label: 'Maintenance',
                                    },
                                    {
                                        value: 'cleaning',
                                        label: 'Cleaning',
                                    },
                                    {
                                        value: 'adminUse',
                                        label: 'Admin Use',
                                    },
                                    {
                                        value: 'accessIssues',
                                        label: 'Access Issues',
                                    },
                                    {
                                        value: 'operationalReason',
                                        label: 'Operational Reason',
                                    },
                                    {
                                        value: 'upgrade',
                                        label: 'Upgrade/Improvements',
                                    },
                                ]}
                            />
                        </Grid>
                    </Grid>

                    <Stack
                        direction='row'
                        sx={{
                            width: '100%',
                            justifyContent: 'flex-end',
                            gap: 1,
                        }}
                    >
                        <Button
                            variant='outlined'
                            onClick={() => {
                                setDraftRange(undefined);
                                reset({ from: '', to: '', reason: '' });
                            }}
                            disabled={!draftRange?.from}
                        >
                            Clear
                        </Button>
                        <Button
                            variant='contained'
                            disabled={!draftRange?.from}
                            type='button'
                            onClick={onNestedSubmit}
                        >
                            Set Unavailable
                        </Button>
                    </Stack>
                </>
            )}

            {unavailablePeriods.filter(
                (p: Period) =>
                    new Date(p.to) >= startOfDay(new Date()) &&
                    p.reason !== 'reserved',
            ).length > 0 && (
                <Stack sx={{ width: '100%', pb: 2 }}>
                    <InputLabel>
                        Unavailable periods{' '}
                        <CustomTooltip
                            title={
                                <Typography>
                                    <List>
                                        <ListItem>
                                            Select the periods during which this
                                            space is unavailable.
                                        </ListItem>
                                        <ListItem>
                                            If no periods are selected, the
                                            space will be available at all
                                            times.
                                        </ListItem>
                                        <ListItem>
                                            Only future periods are displayed.
                                        </ListItem>
                                    </List>
                                </Typography>
                            }
                            placement='right'
                        >
                            <HelpOutline sx={{ fontSize: '16px' }} />
                        </CustomTooltip>
                    </InputLabel>
                    <Stack
                        direction='row'
                        sx={{ width: '100%', flexWrap: 'wrap', gap: 1 }}
                    >
                        {unavailablePeriods
                            .filter(
                                (p: Period) =>
                                    new Date(p.to) >= startOfDay(new Date()) &&
                                    p.reason !== 'reserved',
                            )
                            .map((p: Period, index: number) => (
                                <Chip
                                    key={`${p.from}-${p.to}-${index}`}
                                    label={
                                        p.from === p.to
                                            ? `${formatChipDate(p.from)}${p.reason ? ` (${camelCaseToSentenceCase(p.reason)})` : ''}`
                                            : `${formatChipDate(p.from)} → ${formatChipDate(p.to)}${p.reason ? ` (${camelCaseToSentenceCase(p.reason)})` : ''}`
                                    }
                                    onDelete={() => {
                                        setValue(
                                            'unavailablePeriods',
                                            unavailablePeriods.filter(
                                                (period: Period, i: number) =>
                                                    period.demo_id !==
                                                    p.demo_id,
                                            ),
                                            { shouldDirty: true },
                                        );
                                    }}
                                    variant='outlined'
                                />
                            ))}
                    </Stack>
                </Stack>
            )}
        </Stack>
    );
};

export default SpaceAvailability;
